export type NavDirection = 'forward' | 'back'

const STORAGE_KEY = '__reshet_nav_direction_tracker__'

interface TrackerState {
  stack: string[]
  position: number
}

const readPersistedState = (currentKey: string): TrackerState => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as TrackerState
      if (Array.isArray(parsed.stack) && typeof parsed.position === 'number') {
        return parsed
      }
    }
  } catch {
    // ignore — sessionStorage unavailable or corrupted, fall through
  }
  return { stack: [currentKey], position: 0 }
}

const persistState = (state: TrackerState) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore — sessionStorage unavailable (private mode, quota, etc.)
  }
}

/**
 * Tracks browser back/forward direction across Next 12 navigations. Next's
 * `changeState()` fully replaces `history.state` with its own
 * `{url, as, options, __N, key}` object on every push/replace — any custom
 * field stamped on it gets silently discarded on the very next navigation.
 * `key` is the one field Next itself keeps stable per history entry (it uses
 * it for scroll restoration), so position is tracked in `sessionStorage`,
 * keyed by it — never writing anything back to history.state.
 *
 * This is a module-level singleton, not a factory — there's only one real
 * browser navigation direction. An earlier version let each consumer
 * (NavigationProvider, useNativeAppRouter) construct its own independent
 * tracker; both read/wrote the *same* sessionStorage key, so after a hard
 * reload one tracker's fresh state would collide with the other's
 * already-persisted stack, corrupting the position and making back
 * navigations resolve as forward.
 *
 * Two entry points, driven by two different Next.js hooks:
 *
 * - `handlePopState(key)`: call from `router.beforePopState()`, passing the
 *   `key` off the state object that callback receives. `beforePopState` is
 *   invoked by Next's *own* popstate handler as the very first thing it
 *   does — strictly before it calls `this.change(...)`, which is the only
 *   thing that updates `asPath`. That ordering matters: a `routeChangeComplete`
 *   or native `popstate` listener both react too late for a back
 *   navigation, because something that was already showing (e.g. an
 *   interstitial ad keyed by `asPath`) can see the new `asPath` while
 *   direction is still stale, remount, and fire one more request before
 *   direction catches up. Resolving inside `beforePopState` closes that gap
 *   entirely rather than racing it.
 * - `handleRouteChangeComplete()`: call from Next's `routeChangeComplete`.
 *   The only reliable point for detecting a *forward push* (Next only
 *   writes the new entry's `key` into `history.state` partway through that
 *   transition, after `routeChangeStart`). When the same navigation was
 *   already resolved by `handlePopState()`, this returns that same
 *   already-resolved direction instead of recomputing it.
 *
 * Resolving is idempotent per navigation (tracked via `lastResolvedKey`):
 * calling either method more than once for the *same* history key just
 * replays the cached answer instead of re-running the stack/position logic
 * a second time. This matters because more than one consumer
 * (NavigationProvider, PageViewProvider) independently listens for
 * `routeChangeComplete` on the same event — without idempotency, whichever
 * one runs second would see its own already-updated position and
 * misread "same entry as current position" as a fresh forward navigation.
 */
function createTracker() {
  const initialKey =
    typeof window !== 'undefined'
      ? window.history.state?.key ?? 'initial'
      : 'initial'
  let { stack, position } =
    typeof window !== 'undefined'
      ? readPersistedState(initialKey)
      : { stack: [initialKey], position: 0 }
  let handledByPopState = false
  let lastDirection: NavDirection = 'forward'
  let lastResolvedKey: string | undefined = initialKey

  const resolve = (key: string | undefined): NavDirection => {
    if (typeof key !== 'string') {
      return 'forward'
    }

    if (key === lastResolvedKey) {
      return lastDirection
    }
    lastResolvedKey = key

    const idx = stack.indexOf(key)
    let direction: NavDirection

    if (idx === -1) {
      // Brand-new entry: a forward push. Truncate any stale forward
      // branch (mirrors real browser history when navigating to a new
      // page after having gone back).
      position += 1
      stack = stack.slice(0, position)
      stack.push(key)
      direction = 'forward'
    } else if (idx === position) {
      // replaceState on the current entry — not a real navigation.
      direction = 'forward'
    } else {
      const isBack = idx < position
      position = idx
      direction = isBack ? 'back' : 'forward'
    }

    persistState({ stack, position })
    lastDirection = direction
    return direction
  }

  return {
    handlePopState(key: string | undefined): NavDirection {
      handledByPopState = true
      return resolve(key)
    },
    handleRouteChangeComplete(): NavDirection {
      if (handledByPopState) {
        handledByPopState = false
        return lastDirection
      }
      return resolve(window.history.state?.key)
    },
  }
}

export const navigationDirectionTracker = createTracker()
